import 'components/ordering/quotation/components/modal/modal.js'
import 'components/ordering/quotation/components/request/add-to-cart-button.js'
import 'components/ordering/quotation/components/request/answer-buttons.js'
import 'components/ordering/quotation/components/request/request-button.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { customElement, query, state } from 'lit/decorators.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { css, CSSResultGroup, html, LitElement, nothing } from 'lit'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import {
  activeQuotationContext,
  localeContext,
  NotFetchedYet,
  setDraftQuotationContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { consume } from '@lit/context'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { CSCQuotationRequestDownloadButton } from 'components/ordering/quotation/components/request/download-button.js'
import { CSCQuotationModal } from 'components/ordering/quotation/components/modal/modal.js'


initializeIconsByRole({
  'csc-quotation-request-header.draft-button': { name: 'floppy-disk', style: 'solid' },
  'csc-quotation-request-header.delete-button': { name: 'trash-can', style: 'solid' },
  'csc-quotation-request-header.new-request-button': { name: 'plus', style: 'solid' },
  'csc-quotation-request-header.add-to-cart-button': { name: 'shopping-cart', style: 'regular' },
})


const translations = {
  delete_button: t('delete_button', {
    ns: 'csc-quotation-request-header',
    default: 'Delete'
  }),
  new_request_button: t('new_request_button', {
    ns: 'csc-quotation-request-header',
    default: 'New request from this quotation'
  })
}

@customElement('csc-quotation-request-header')
export class CSCQuotationRequestHeader extends LitElement {
  static override styles: CSSResultGroup = css`
    span[slot="top-chips"] {
      display: contents;
    }
    csc-quotation-modal {
      position: absolute;
    }
  `

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @state()
  smallScreen: boolean = false

  constructor() {
    super()

    this.breakpoints.matchMedia(breakpoints.medium)

    createEffect(() => {
      this.smallScreen = this.breakpoints.small()
    })
  }

  @consume({ context: setDraftQuotationContext })
  private setDraftQuotation!: (quotationId: number) => Promise<void>

  async setDraft(): Promise<void> {
    if (!isQuotationDTO(this.quotation)) return
    await this.setDraftQuotation(this.quotation.id)
  }

  @query('csc-quotation-modal[target-id="reopen"]')
  private reopenModal: CSCQuotationModal | null | undefined

  @query('csc-quotation-modal[target-id="delete"]')
  private deleteModal!: CSCQuotationModal | null | undefined

  @consume({ context: localeContext })
  private locale!: Intl.LocalesArgument

  get createDate() {
    if (!isQuotationDTO(this.quotation) || this.locale === undefined) return

    return new Intl.DateTimeFormat(this.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(this.quotation.createDate))
  }

  renderButtons() {
    if (!isQuotationDTO(this.quotation)) {
      return nothing
    }

    if (this.quotation.status === 'open') {
      return this.renderOpenButtons()
    }
    if (this.quotation.status === 'draft') {
      return this.renderDraftButtons()
    }
    if ([
      'answered',
      'accepted',
      'confirmed',
      'declined',
      'declined_by_merchant',
      'overdue'
    ].includes(String(this.quotation.status))) {
      return this.renderFinalButtons()
    }

    return nothing
  }

  renderDeleteButton() {
    return html`
      <cse-chip @click=${() => this.deleteModal?.open()}
      >
        <cse-icon icon-role="csc-quotation-request-header.delete-button" slot="prefix"></cse-icon>
        ${translations.delete_button}
      </cse-chip>

      <csc-quotation-modal target-id="delete" type="delete"></csc-quotation-modal>
    `
  }

  renderNewRequestButton() {
    if (!isQuotationDTO(this.quotation) || !this.quotation.canCreateNewQuotation || !this.quotation.isClosed) {
      return nothing
    }

    return html`
      <cse-chip @click="${() => this.reopenModal?.open()}">
        <cse-icon icon-role="csc-quotation-request-header.new-request-button" slot="prefix"></cse-icon>
        ${translations.new_request_button}
      </cse-chip>

      <csc-quotation-modal target-id="reopen" type="reopen"></csc-quotation-modal>
    `
  }

  renderDownloadButton() {
    if (!isQuotationDTO(this.quotation) || !this.quotation.quotationDocumentUrl) {
      return nothing
    }

    return new CSCQuotationRequestDownloadButton()
  }

  renderOpenButtons() {
    return html`
      <cse-chip @click=${this.setDraft}>
        <cse-icon icon-role="csc-quotation-request-header.draft-button" slot="prefix"></cse-icon>
        ${t('draft_button', {
          ns: 'csc-quotation-request-header',
          default: 'Save as draft'
        })}
      </cse-chip>

      ${this.renderDeleteButton()}
    `
  }

  renderDraftButtons() {
    return html`
      <cse-chip @click=${() => this.reopenModal?.open()}>
        ${t('reopen_draft_button', {
          ns: 'csc-quotation-request-header',
          default: 'Activate quotation'
        })}
      </cse-chip>

      ${this.renderDeleteButton()}

      <csc-quotation-modal target-id="reopen" type="reopen"></csc-quotation-modal>
    `
  }

  renderFinalButtons() {
    return html`
      ${this.renderNewRequestButton()}
      ${this.renderDownloadButton()}
    `
  }

  get hasBottomButtons(): boolean {
    if (!isQuotationDTO(this.quotation)) return false
    const showsRequestButton = this.quotation.status === 'open'
    const showsAnswerButtons = this.quotation.status === 'answered' && !this.quotation.priceRequestMode
    const showsAddToCartButton = this.quotation.status === 'accepted'
      && this.quotation.canAddToBasket
      && this.quotation.canApprove
    return showsRequestButton || showsAnswerButtons || showsAddToCartButton
  }

  override render() {
    if (!isQuotationDTO(this.quotation)) {
      return nothing
    }

    return html`
      <csc-account-header ?hide-divider=${!this.hasBottomButtons}>
        <span slot="heading">
          ${t('title', {
            ns: 'csc-quotation-request-header',
            default: 'Quotation {{variable_code}}',
            variable_code: [this.quotation?.internalReference, this.quotation?.externalReference].filter(Boolean).join(' / ') || '-'
          })}
        </span>

        ${this.quotation ? html`
          <csc-quotation-status slot="badge" status="${this.quotation.status}"></csc-quotation-status>

          <span slot="date">
            ${this.createDate}
          </span>

          <span slot="top-chips">
            ${this.renderButtons()}
          </span>

          <span slot="bottom-buttons">
            <csc-quotation-request-button></csc-quotation-request-button>
            <csc-quotation-answer-buttons></csc-quotation-answer-buttons>
            <csc-quotation-add-to-cart-button></csc-quotation-add-to-cart-button>
          </span>
        ` : nothing}
      </csc-account-header>
    `
  }
}
